// File: src/SignaturePage.js
// Final signature confirmation page for driver verification
// v5.0: Oct 2026 redesign — StepShell / PlainShell, declarations as ticks
//       (every one must be ticked before signing), referral-aware finish screen.
// v4.2: Added licenseEnding to generateSummary() for confirmation email
// v4.1: Added friendly error messages with retry button for better UX
// v4.0: Added session token authentication for driver-status calls
// v4.0: Added post-signature notifications (mid-tour + manual review alerts)
// v3.9: Add driver verification snapshot PDF generation for audit trail
// v3.8: Add automatic additional driver charge after successful Board B copy
// v3.7: Pass signature URL directly to copyAToB for reliable Board B copy

import React, { useState, useEffect, useRef } from 'react';
import { isUkIssuer, isNiIssuer } from './utils/licenceIssuer';
import reportError from './utils/errorReporter';
import {
  StepShell, PlainShell, Card, PrimaryButton, CheckRow, SummaryRow, Spinner,
  PHASE, TERMS_URL, HELP_EMAIL, formatHireRange,
} from './ui/HireFormUI';

// Module scope on purpose — never declare a component inside another one
// (CLAUDE.md, "Never declare a component inside another component").
const DocStatus = ({ valid }) => (
  valid ? <span className="hf-pill">Verified</span> : 'Pending'
);

/** "Job #16281 · 24–27 Oct", or just "Job #16281" when the dates aren't known. */
function hireLabel(jobId, jobDetails) {
  const number = jobDetails?.jobNumber || jobId;
  if (!number) return null;
  const range = jobDetails ? formatHireRange(jobDetails.startDate, jobDetails.endDate) : null;
  return range ? `Job #${number} · ${range}` : `Job #${number}`;
}

const SignaturePage = ({ driverEmail: propEmail, jobId: propJobId }) => {
  // Support both props and URL params for testing
  const urlParams = new URLSearchParams(window.location.search);
  const driverEmail = propEmail || urlParams.get('email');
  const jobId = propJobId || urlParams.get('job');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [driverData, setDriverData] = useState(null);
  const [jobDetails, setJobDetails] = useState(null);
  const [signature, setSignature] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState(null); // v4.1: Enhanced error state
  const [completed, setCompleted] = useState(false);
  const [ticks, setTicks] = useState({}); // declaration id -> ticked
  
  const canvasRef = useRef(null);
  const minDrawingLength = useRef(0);

  // v4.0: Helper to get session token for authenticated API calls
  const getSessionToken = () => {
    return sessionStorage.getItem('verificationSessionToken') || '';
  };



  // Load driver and job data
  useEffect(() => {
    const loadData = async () => {
      if (!driverEmail) {
        setError('No email provided. Please use ?email=driver@email.com in the URL');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        // v4.0: Added session token header for authenticated API call
        const driverResponse = await fetch(`/.netlify/functions/driver-status?email=${encodeURIComponent(driverEmail)}`, {
          headers: {
            'X-Session-Token': getSessionToken()
          }
        });
        if (driverResponse.ok) {
          const driver = await driverResponse.json();
          setDriverData(driver);
          console.log('Driver data loaded:', driver);
        } else {
          throw new Error('Failed to load driver data');
        }
        
        // Get job details if job ID provided
        if (jobId) {
          const jobResponse = await fetch(`/.netlify/functions/validate-job?jobId=${jobId}`);
          if (jobResponse.ok) {
            const jobData = await jobResponse.json();
            setJobDetails(jobData.job);
            console.log('Job details loaded:', jobData.job);
          } else {
            console.warn('Could not load job details');
          }
        }
        
      } catch (err) {
        console.error('Error loading data:', err);
        setError('Failed to load verification details. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [driverEmail, jobId]);

  // Initialize canvas. Runs again once loading finishes: on mount the page is
  // still loading and the canvas isn't rendered yet, so with [] this returned
  // early and never ran again — every signature was drawn with the canvas
  // defaults (1px square-ended lines) instead of these.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
  }, [loading, driverData]);

  // Handle drawing — v3.8: scale mouse/touch coordinates to canvas resolution
  // The canvas HTML resolution (width/height attrs) may differ from the CSS
  // display size (w-full + height:auto), so we must scale to avoid cramped drawing.
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
    const clientY = e.type.includes('mouse') ? e.clientY : e.touches[0].clientY;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoords(e);

    ctx.beginPath();
    ctx.moveTo(x, y);
    minDrawingLength.current = 0;
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCanvasCoords(e);

    ctx.lineTo(x, y);
    ctx.stroke();
    minDrawingLength.current += 2;
    setHasDrawn(true);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      // Only capture if minimum drawing achieved (prevents accidental dots)
      if (minDrawingLength.current > 50) {
        const canvas = canvasRef.current;
        setSignature(canvas.toDataURL('image/png'));
      }
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignature(null);
    setHasDrawn(false);
    minDrawingLength.current = 0;
  };

  // v3.8: Process additional driver charges after Board B copy
  const processAdditionalDriverCharge = async () => {
    if (!jobId) {
      console.log('No job ID - skipping additional driver charge check');
      return;
    }

    try {
      console.log('🚗 Checking for additional driver charges...');
      
      const chargeResponse = await fetch('/.netlify/functions/additional-driver-charge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jobId: jobId,
          email: driverEmail
        })
      });

      if (chargeResponse.ok) {
        const chargeResult = await chargeResponse.json();
        console.log('🚗 Additional driver charge result:', chargeResult);
        
        if (chargeResult.chargesAdded > 0) {
          console.log(`✅ Added ${chargeResult.chargesAdded} additional driver charge(s)`);
        } else if (chargeResult.manualActionRequired) {
          console.log('⚠️ Manual action required for additional driver charges');
        } else {
          console.log('✅ No additional driver charges needed');
        }
      } else {
        console.warn('⚠️ Additional driver charge check failed - continuing anyway');
      }
    } catch (err) {
      // Don't fail the whole verification if charge processing fails
      console.error('⚠️ Error processing additional driver charges:', err);
    }
  };

  // v3.9: Generate driver verification snapshot PDF for audit trail
  const processDriverSnapshot = async () => {
    if (!jobId) {
      console.log('No job ID - skipping driver snapshot');
      return;
    }

    try {
      console.log('📸 Generating driver verification snapshot...');
      
      const snapshotResponse = await fetch('/.netlify/functions/generate-driver-snapshot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: driverEmail,
          jobId: jobId
        })
      });

      if (snapshotResponse.ok) {
        const snapshotResult = await snapshotResponse.json();
        console.log('📸 Driver snapshot result:', snapshotResult);
        
        if (snapshotResult.success) {
          console.log(`✅ Driver snapshot created: ${snapshotResult.filename}`);
        } else {
          console.warn('⚠️ Snapshot generation failed:', snapshotResult.error);
        }
      } else {
        console.warn('⚠️ Snapshot generation request failed - continuing anyway');
      }
    } catch (err) {
      // Don't fail the whole verification if snapshot fails - it's nice-to-have
      console.error('⚠️ Error generating driver snapshot:', err);
    }
  };

  // v4.0: Send post-signature notifications (mid-tour alerts + manual review summaries)
  const processPostSignatureNotifications = async () => {
    try {
      console.log('📬 Processing post-signature notifications...');
      
      const notifyResponse = await fetch('/.netlify/functions/post-signature-notifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: driverEmail,
          jobId: jobId,
          driverName: driverData?.name || driverData?.driverName || null
        })
      });

      if (notifyResponse.ok) {
        const notifyResult = await notifyResponse.json();
        console.log('📬 Notification result:', notifyResult);
        
        if (notifyResult.midTourNotification?.sent) {
          console.log('🚨 Mid-tour notification sent');
        }
        if (notifyResult.reviewNotification?.sent) {
          console.log('📋 Manual review notification sent');
        }
      } else {
        console.warn('⚠️ Notification request failed - continuing anyway');
      }
    } catch (err) {
      // Don't fail verification if notifications fail
      console.error('⚠️ Error processing notifications:', err);
    }
  };

  // v5.8: Report a client-side OP-step failure to the server so it reaches
  // the same telemetry endpoint as server-side Monday fallbacks. Fire-and-forget.
  const logClientFallback = (operation, err) => {
    try {
      fetch('/.netlify/functions/log-client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page: 'SignaturePage',
          error: `${operation} failed: ${err?.message || String(err)}`,
          severity: 'warning',
          email: driverEmail,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          additionalInfo: `operation=${operation}`,
        }),
      }).catch(() => {});
    } catch (_) {
      // best effort
    }
  };

  // v4.1: Enhanced error handling with user-friendly messages
  const handleApiError = (response, fallbackMessage) => {
    try {
      const data = response;
      return {
        message: data.userMessage || fallbackMessage,
        technical: data.error || data.details || 'Unknown error',
        retryable: data.retryable !== false // Default to true if not specified
      };
    } catch {
      return {
        message: fallbackMessage,
        technical: 'Could not parse error response',
        retryable: true
      };
    }
  };

  const saveSignatureAndComplete = async () => {
    if (!signature) {
      setError('Please provide your signature');
      return;
    }
    
    setSaving(true);
    setError('');
    setErrorDetails(null);
    
    try {
      // Step 1: Save signature image to Monday Board A
      const signatureData = signature.split(',')[1]; // Remove data:image/png;base64, prefix
      const signatureDate = new Date().toISOString().split('T')[0];
      const signatureFilename = `signature_${Date.now()}.png`;
      
      const saveResponse = await fetch('/.netlify/functions/monday-integration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'upload-file-board-a',
          email: driverEmail,
          fileType: 'signature',
          fileData: signatureData,
          filename: signatureFilename,
          contentType: 'image/png',
          clearExisting: true
        })
      });
      
      if (!saveResponse.ok) {
        const errorData = await saveResponse.json().catch(() => ({}));
        const errorInfo = handleApiError(errorData, 'We couldn\'t save your signature. Please try again.');
        setError(errorInfo.message);
        setErrorDetails(errorInfo);
        setSaving(false);
        return;
      }
      
      // v3.7: Capture the file URL for passing to Board B copy
      const saveResult = await saveResponse.json();
      const signatureFileUrl = saveResult.fileUrl;
      console.log('Signature uploaded, URL:', signatureFileUrl);
      
      // Step 2: Update signature date and status in Board A
      const updateResponse = await fetch('/.netlify/functions/monday-integration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update-driver-board-a',
          email: driverEmail,
          updates: {
            signatureDate: signatureDate,
            overallStatus: 'Verified',
            completionTimestamp: new Date().toISOString()
          }
        })
      });

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json().catch(() => ({}));
        const errorInfo = handleApiError(errorData, 'We couldn\'t update your verification status. Please try again.');
        setError(errorInfo.message);
        setErrorDetails(errorInfo);
        setSaving(false);
        return;
      }
      
      // Step 3: Copy driver from Board A to Board B if job ID exists
      if (jobId) {
        console.log('Copying driver to Board B with job:', jobId);
        const copyResponse = await fetch('/.netlify/functions/monday-integration', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'copy-a-to-b',
            email: driverEmail,
            jobId: jobId,
            signatureData: signatureData,  // Pass raw base64 for direct upload to Board B
            signatureFilename: `signature_${Date.now()}.png`
          })
        });
        
        if (!copyResponse.ok) {
          // This step is the ONLY thing that joins the driver to the hire
          // (it creates the hire assignment in OP). It used to log and carry
          // on, so the driver was told "Verification Complete" while OP had
          // them as not signed. Stop here instead: their signature and
          // details are already saved, and "Try Again" re-runs the chain,
          // which is safe because OP de-duplicates on (driver, job).
          const errorData = await copyResponse.json().catch(() => ({}));
          const errorInfo = handleApiError(
            errorData,
            'We couldn\'t add you to this hire. Your signature is saved — please try again.'
          );
          reportError({
            page: 'SignaturePage',
            error: `copy-a-to-b failed (HTTP ${copyResponse.status}): ${errorInfo.technical}`,
            severity: 'critical',
            email: driverEmail,
            additionalInfo: `jobId=${jobId}`,
          });
          setError(errorInfo.message);
          setErrorDetails(errorInfo);
          setSaving(false);
          return;
        } else {
          const copyResult = await copyResponse.json();
          console.log('Board B copy result:', copyResult);

          if (copyResult.assignmentId) {
            // v5.8: OP mode — chain PDF generation then post-signature automations.
            // Each step awaits the previous. Step B/C failures are logged but
            // non-blocking: the assignment already exists and the driver has signed,
            // so we still show completion and let ops chase up any stragglers manually.
            const assignmentId = copyResult.assignmentId;

            // Step B: generate hire form PDF + email it to the driver via OP
            try {
              const pdfResponse = await fetch('/.netlify/functions/monday-integration', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  action: 'generate-pdf',
                  assignmentId,
                  email: driverEmail,
                })
              });
              if (!pdfResponse.ok) {
                const body = await pdfResponse.text().catch(() => '');
                throw new Error(`generate-pdf returned ${pdfResponse.status}: ${body.slice(0, 200)}`);
              }
              console.log('[SignaturePage] PDF generated and emailed via OP');
            } catch (pdfErr) {
              console.error('[SignaturePage] PDF generation failed (non-blocking):', pdfErr.message);
              logClientFallback('generate-pdf', pdfErr);
            }

            // Step C: post-signature automations (additional driver charge + mid-tour check)
            try {
              const postResponse = await fetch('/.netlify/functions/monday-integration', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  action: 'post-signature',
                  assignmentId,
                  email: driverEmail,
                })
              });
              if (!postResponse.ok) {
                const body = await postResponse.text().catch(() => '');
                throw new Error(`post-signature returned ${postResponse.status}: ${body.slice(0, 200)}`);
              }
              console.log('[SignaturePage] Post-signature automations completed via OP');
            } catch (postErr) {
              console.error('[SignaturePage] Post-signature failed (non-blocking):', postErr.message);
              logClientFallback('post-signature', postErr);
            }
          } else {
            // Monday.com mode — existing behavior
            // v4.3: Trigger hire form PDF generation via webhook
            if (copyResult.hireFormId) {
              try {
                console.log('Triggering hire form PDF generation for:', copyResult.hireFormId);
                await fetch('/.netlify/functions/generate-hire-form', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    event: {
                      pulseId: copyResult.hireFormId
                    }
                  })
                });
              } catch (pdfErr) {
                console.warn('Hire form PDF generation failed (non-blocking):', pdfErr.message);
              }
            }

            // v4.0: Run all post-signature automations in parallel
            // - Additional driver charge check (adds £20+VAT per extra driver to Hirehop)
            // - Driver snapshot PDF (audit trail of verification state at time of signing)
            // - Post-signature notifications (mid-tour alerts + manual review summaries)
            await Promise.all([
              processAdditionalDriverCharge(),
              processDriverSnapshot(),
              processPostSignatureNotifications()
            ]);
          }
        }
      }
      
      // Step 4: Send confirmation email
      await sendConfirmationEmail(signatureDate);
      
      setCompleted(true);
      
    } catch (err) {
      console.error('Error saving signature:', err);
      setError('Something went wrong. Please try again.');
      setErrorDetails({
        message: 'Something went wrong. Please try again.',
        technical: err.message,
        retryable: true
      });
    } finally {
      setSaving(false);
    }
  };

  const sendConfirmationEmail = async (signatureDate) => {
    try {
      const summary = generateSummary();
      
      await fetch('/.netlify/functions/send-confirmation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: driverEmail,
          jobId: jobId,
          summary: summary,
          driverName: driverData?.name || driverData?.driverName || 'Driver',
          jobDetails: jobDetails,
          signatureDate: signatureDate
        })
      });
      
    } catch (err) {
      console.error('Error sending confirmation email:', err);
      // Don't fail the whole process if email fails
    }
  };

  const generateSummary = () => {
    if (!driverData) return {};
    
    return {
      name: driverData.name || driverData.driverName || 'Not provided',
      email: driverEmail,
      phone: `${driverData.phoneCountry || ''} ${driverData.phoneNumber || ''}`,
      nationality: driverData.nationality || 'Not provided',
      dateOfBirth: driverData.dateOfBirth || 'Not provided',
      licenseNumber: driverData.licenseNumber || 'Not provided',
      // v4.2: Added licenseEnding for confirmation email
      licenseEnding: driverData.licenseEnding || null,
      licenseIssuedBy: driverData.licenseIssuedBy || 'Not provided',
      // v4.3: Show the licence card's real expiry, not our 90-day re-check window.
      // documents.license.expiryDate is OP's computed min(licence_valid_to,
      // licence_next_check_due) — great for ticks, wrong for "valid until" text.
      licenseValidTo: driverData.licenseValidTo || driverData.documents?.license?.expiryDate || null,
      datePassedTest: driverData.datePassedTest || driverData.insuranceData?.datePassedTest || null,
      homeAddress: driverData.homeAddress || 'Not provided',
      licenseAddress: driverData.licenseAddress || 'Not provided',
      dvlaPoints: driverData.dvlaPoints,
      dvlaEndorsements: driverData.dvlaEndorsements,
      dvlaCalculatedExcess: driverData.dvlaCalculatedExcess,
      insuranceQuestions: {
        hasDisability: driverData.hasDisability || driverData.insuranceData?.hasDisability || false,
        hasConvictions: driverData.hasConvictions || driverData.insuranceData?.hasConvictions || false,
        hasProsecution: driverData.hasProsecution || driverData.insuranceData?.hasProsecution || false,
        hasAccidents: driverData.hasAccidents || driverData.insuranceData?.hasAccidents || false,
        hasInsuranceIssues: driverData.hasInsuranceIssues || driverData.insuranceData?.hasInsuranceIssues || false,
        hasDrivingBan: driverData.hasDrivingBan || driverData.insuranceData?.hasDrivingBan || false,
        hasRecentConvictions: driverData.insuranceData?.hasRecentConvictions ?? null,
        hasFaultClaims: driverData.insuranceData?.hasFaultClaims ?? null,
        additionalDetails: driverData.additionalDetails || driverData.insuranceData?.additionalDetails || ''
      },
      documents: {
        license: driverData.documents?.license?.valid || false,
        poa1: driverData.documents?.poa1?.valid || false,
        poa2: driverData.documents?.poa2?.valid || false,
        dvlaCheck: driverData.documents?.dvlaCheck?.valid || false,
        passport: driverData.documents?.passport?.valid || driverData.documents?.passportCheck?.valid || false
      }
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatYesNo = (value) => {
    if (value === true || value === 'yes' || value === 'Yes') return 'Yes';
    if (value === false || value === 'no' || value === 'No') return 'No';
    return 'Not answered';
  };


  const driverName = driverData?.name || driverData?.driverName || null;
  const hire = hireLabel(jobId, jobDetails);

  // Loading state
  if (loading) {
    return (
      <StepShell jobId={jobId} phase={PHASE.SIGN}>
        <Card>
          <Spinner />
          <p className="hf-intro hf-center">Loading your summary…</p>
        </Card>
      </StepShell>
    );
  }

  // Error state (no email)
  if (!driverEmail && !loading) {
    return (
      <PlainShell>
        <Card>
          <h2 className="hf-h2">We need your email address</h2>
          <p className="hf-intro">
            This page needs to know who you are. Please open it from the link in your email.
          </p>
        </Card>
      </PlainShell>
    );
  }

  // Couldn't load the driver's details. The main form below renders nothing
  // (not even the error) without driverData, so this used to be a dead end:
  // a heading and an empty card. Give the driver a way out.
  if (!driverData) {
    return (
      <StepShell jobId={jobId} phase={PHASE.SIGN}>
        <Card>
          <h2 className="hf-h2">We couldn't load your summary</h2>
          <p className="hf-intro">
            {error || 'Failed to load verification details. Please try again.'}
            {' '}Nothing you've done so far has been lost.
          </p>
          <PrimaryButton onClick={() => window.location.reload()}>Try again</PrimaryButton>
          <p className="hf-small">
            Still stuck? Email us at{' '}
            <a href={`mailto:${HELP_EMAIL}`} className="hf-link">{HELP_EMAIL}</a>.
          </p>
        </Card>
      </StepShell>
    );
  }

  // Completed state. Two endings: a clean one, and one where a referral is
  // still open with the insurer, so the driver mustn't be told they're
  // approved to drive. `referralOpen` comes from the status payload; anything
  // other than `true` counts as no referral.
  if (completed) {
    const referralOpen = driverData.referralOpen === true;
    return (
      <PlainShell>
        <Card>
          <div className={`hf-big-tick${referralOpen ? ' is-amber' : ''}`} aria-hidden="true">✓</div>
          <p className={`hf-eyebrow${referralOpen ? ' is-amber' : ''}`}>
            {referralOpen ? 'Signed' : 'Form complete'}
          </p>
          <h1 className="hf-h1 hf-center">
            {referralOpen ? "You've signed — one more check on our side" : "You're all done"}
          </h1>
          <p className="hf-intro hf-center">
            {referralOpen
              ? "Something on your form needs our insurer's OK before you can drive. We've passed it on and we'll be in touch. You don't need to do anything else."
              : "There's nothing else you need to do. You can close this page now."}
          </p>
          <div className="hf-inset hf-sig-done-summary">
            <SummaryRow label="Driver" value={driverName || driverEmail} />
            {hire && <SummaryRow label={referralOpen ? 'Hire' : 'Approved for'} value={hire} />}
            <SummaryRow label="Signed copy" value="Emailed to you" />
          </div>
        </Card>
        <Card>
          <p className="hf-body">
            <strong className="hf-strong">Anyone else driving?</strong> Each driver needs to fill in
            their own form. Forward them the link from your email.
          </p>
        </Card>
      </PlainShell>
    );
  }

  // Licence record check results — UK drivers only, Northern Ireland
  // included. `isUkIssuer`, NOT `=== 'DVLA'`: a DVA licence is a UK licence,
  // and the strict comparison hid a NI driver's own points and excess from them.
  const showLicenceCheck = isUkIssuer(driverData.licenseIssuedBy)
    && (driverData.dvlaPoints !== undefined || driverData.dvlaEndorsements || driverData.dvlaCalculatedExcess);

  const hasAccidents = driverData.hasAccidents || driverData.insuranceData?.hasAccidents;
  const additionalDetails = driverData.additionalDetails || driverData.insuranceData?.additionalDetails;

  // The declarations, one tick each. Every one shown must be ticked before the
  // driver can sign.
  const declarations = [
    { id: 'true', text: 'All the information I have provided is true and accurate' },
    {
      id: 'terms',
      // Plain text: a link can't sit inside the tick row (it's a <button>).
      // The terms are linked just above the ticks and again under the button.
      text: 'I have read and agree to the terms and conditions of hire',
    },
    { id: 'false-info', text: 'If I provide false information this insurance may be invalidated' },
    { id: 'entitled', text: 'I am legally entitled to drive in the UK' },
  ];
  // The driver must be told the excess they are agreeing to.
  // `=== 'DVLA'` withheld this line from Northern Ireland
  // drivers, who signed the declaration with no excess stated.
  if (isUkIssuer(driverData.licenseIssuedBy) && driverData.dvlaCalculatedExcess) {
    declarations.push({
      id: 'excess',
      text: (
        <>
          I understand my insurance excess is <strong>{driverData.dvlaCalculatedExcess}</strong> payable
          in the event of a claim
        </>
      ),
    });
  }
  const allTicked = declarations.every((d) => ticks[d.id]);
  const readyToSign = allTicked && !!signature;
  const toggleTick = (id) => setTicks((prev) => ({ ...prev, [id]: !prev[id] }));

  // Main signature form
  return (
    <StepShell jobId={jobId} phase={PHASE.SIGN}>
      <Card>
        <div>
          <h2 className="hf-h2">Check and sign</h2>
          <p className="hf-intro" style={{ marginTop: 6 }}>Last step. Once you sign, you're done.</p>
        </div>
        <div>
          <div className="hf-summary">
            <SummaryRow label="Driver" value={driverName || 'Not provided'} />
            {hire && <SummaryRow label="Hire" value={hire} />}
            {showLicenceCheck && (
              <>
                <SummaryRow
                  label="Licence points"
                  value={driverData.dvlaPoints === 0 ? 'Clean licence' : `${driverData.dvlaPoints} points`}
                />
                <SummaryRow
                  label="Your insurance excess"
                  value={driverData.dvlaCalculatedExcess || '£1,200'}
                  strong
                />
              </>
            )}
          </div>
          {showLicenceCheck && (
            <p className="hf-small" style={{ marginTop: 10 }}>
              The excess is the most you'd pay towards damage if something goes wrong during the hire.
            </p>
          )}
        </div>
      </Card>

      <Card className="hf-card-tight">
        <h2 className="hf-h2">Your details</h2>
        <div className="hf-summary">
          <SummaryRow label="Email" value={driverEmail} />
          <SummaryRow
            label="Phone"
            value={`${driverData.phoneCountry || ''} ${driverData.phoneNumber || 'Not provided'}`.trim()}
          />
          <SummaryRow label="Nationality" value={driverData.nationality || 'Not provided'} />
          <SummaryRow label="Date of birth" value={formatDate(driverData.dateOfBirth)} />
          <SummaryRow label="Licence number" value={driverData.licenseNumber || 'Not provided'} />
          <SummaryRow
            label="Date passed test"
            value={formatDate(driverData.datePassedTest || driverData.insuranceData?.datePassedTest)}
          />
          {showLicenceCheck && (
            <SummaryRow label="Endorsements" value={driverData.dvlaEndorsements || 'None'} />
          )}
          <SummaryRow label="Home address" value={driverData.homeAddress || 'Not provided'} />
          <SummaryRow label="Licence address" value={driverData.licenseAddress || 'Not provided'} />
        </div>
      </Card>

      <Card className="hf-card-tight">
        <h2 className="hf-h2">Your answers</h2>
        <div className="hf-summary">
          <SummaryRow
            label="Told not to drive, or driving restricted (medical)?"
            value={formatYesNo(driverData.hasDisability || driverData.insuranceData?.hasDisability)}
          />
          <SummaryRow
            label="Any motoring convictions in the last 5 years?"
            value={formatYesNo(driverData.insuranceData?.hasRecentConvictions)}
          />
          <SummaryRow
            label="Any serious motoring convictions?"
            value={formatYesNo(driverData.hasConvictions || driverData.insuranceData?.hasConvictions)}
          />
          <SummaryRow
            label="Prosecution pending?"
            value={formatYesNo(driverData.hasProsecution || driverData.insuranceData?.hasProsecution)}
          />
          <SummaryRow label="Any accidents or claims in the last 5 years?" value={formatYesNo(hasAccidents)} />
          {hasAccidents && (
            <SummaryRow
              label="More than one at fault or still open?"
              value={formatYesNo(driverData.insuranceData?.hasFaultClaims)}
            />
          )}
          <SummaryRow
            label="Refused motor insurance or special terms?"
            value={formatYesNo(driverData.hasInsuranceIssues || driverData.insuranceData?.hasInsuranceIssues)}
          />
          <SummaryRow
            label="Banned, disqualified or suspended in the last 5 years?"
            value={formatYesNo(driverData.hasDrivingBan || driverData.insuranceData?.hasDrivingBan)}
          />
        </div>
        {additionalDetails && (
          <div className="hf-inset">
            <p className="hf-small">Anything else you told us</p>
            <p className="hf-body" style={{ marginTop: 4 }}>{additionalDetails}</p>
          </div>
        )}
      </Card>

      <Card className="hf-card-tight">
        <h2 className="hf-h2">Documents</h2>
        <div className="hf-summary">
          <SummaryRow label="Driving licence" value={<DocStatus valid={driverData.documents?.license?.valid} />} />
          <SummaryRow label="Proof of address 1" value={<DocStatus valid={driverData.documents?.poa1?.valid} />} />
          <SummaryRow label="Proof of address 2" value={<DocStatus valid={driverData.documents?.poa2?.valid} />} />
          {/* Conditional: licence record check OR passport, by issuer.
              A DVA driver was previously shown the PASSPORT row — a
              document they may not have and were never asked for. */}
          {isUkIssuer(driverData.licenseIssuedBy) ? (
            <SummaryRow
              label={isNiIssuer(driverData.licenseIssuedBy) ? 'DVA licence check' : 'DVLA check'}
              value={<DocStatus valid={driverData.documents?.dvlaCheck?.valid} />}
            />
          ) : (
            <SummaryRow
              label="Passport"
              value={<DocStatus valid={driverData.documents?.passportCheck?.valid || driverData.documents?.passport?.valid} />}
            />
          )}
        </div>
      </Card>

      <Card>
        <div>
          <p className="hf-small">
            By my signature, I acknowledge and confirm that (
            <a className="hf-link" href={TERMS_URL} target="_blank" rel="noopener noreferrer">read the hire terms</a>):
          </p>
          {declarations.map((d) => (
            <CheckRow key={d.id} checked={!!ticks[d.id]} onToggle={() => toggleTick(d.id)}>
              {d.text}
            </CheckRow>
          ))}
        </div>

        {/* Signature pad. The look (inset, border, radius) is on the WRAPPER:
            padding or a border on the canvas itself would throw out the
            getBoundingClientRect scaling in getCanvasCoords. */}
        <div className="hf-field">
          <div className="hf-sig-label-row">
            <span className="hf-label">Your signature</span>
            <button type="button" className="hf-link-btn" onClick={clearSignature} disabled={!hasDrawn}>
              Clear
            </button>
          </div>
          <div className="hf-sig-pad">
            <canvas
              ref={canvasRef}
              width={600}
              height={200}
              className="hf-sig-canvas"
              style={{ touchAction: 'none' }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
            {!hasDrawn && <span className="hf-sig-hint" aria-hidden="true">Sign here with your finger</span>}
          </div>
          <p className="hf-small">
            {signature ? 'Signature captured' : hasDrawn ? 'Draw a little more to capture your signature' : 'Please sign in the box above'}
          </p>
        </div>
      </Card>

      {/* v4.1: Enhanced Error Display with Retry */}
      {error && (
        <div className="hf-note hf-note-error" role="alert">
          <p className="hf-strong" style={{ margin: 0, color: 'inherit' }}>
            {errorDetails?.retryable ? 'A temporary problem' : 'Something went wrong'}
          </p>
          <p style={{ margin: '4px 0 0' }}>{error}</p>
          {errorDetails?.retryable && (
            <p style={{ margin: '6px 0 0' }}>
              It may just be the connection. Your signature is still here, so please try again.
            </p>
          )}
          <div className="hf-row" style={{ marginTop: 12, flexWrap: 'wrap' }}>
            {errorDetails?.retryable && (
              <button
                type="button"
                className="hf-btn-small"
                onClick={saveSignatureAndComplete}
                disabled={saving}
              >
                {saving ? 'Retrying…' : 'Try again'}
              </button>
            )}
            <a
              href={`mailto:${HELP_EMAIL}?subject=Driver%20Verification%20Issue`}
              className="hf-btn-small hf-sig-support"
            >
              Contact support
            </a>
          </div>
        </div>
      )}

      <div className="hf-field" style={{ gap: 10 }}>
        <PrimaryButton
          onClick={saveSignatureAndComplete}
          disabled={!readyToSign}
          busy={saving}
          busyLabel="Saving…"
        >
          {readyToSign ? 'Sign and finish' : 'Tick each box and sign to finish'}
        </PrimaryButton>
        <p className="hf-small hf-center">
          <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="hf-link" style={{ fontWeight: 400 }}>
            Read the full hire terms
          </a>
          {' · '}We'll email you a signed copy
        </p>
        <div className="hf-center">
          <button
            type="button"
            className="hf-link-btn"
            onClick={() => window.history.back()}
            disabled={saving}
          >
            Back
          </button>
        </div>
      </div>
    </StepShell>
  );
};

export default SignaturePage;
